import { queryClient } from './queryClient';
import { APIGetRequest } from 'util/connection';
import { ApiResponse } from 'constants/ActionTypes';

type RoomPlanQueryScope = 'resource' | 'event';
type RoomPlanQueryAuthContext = {
  prop_id?: string | number | null;
  user_id?: string | number | null;
  log_id?: string | number | null;
};

export const ROOMPLAN_FORCE_REFRESH_FLAG = '__roomplanForceRefresh';

const ROOMPLAN_QUERY_STALE_TIME: Record<RoomPlanQueryScope, number> = {
  resource: 2 * 60 * 1000,
  event: 30 * 1000,
};

const ROOMPLAN_QUERY_CACHE_TIME = 10 * 60 * 1000;

export const roomPlanQueryKeys = {
  all: ['roomPlan'] as const,
  resources: ['roomPlan', 'resources'] as const,
  events: ['roomPlan', 'events'] as const,
  resource: (authScope: string, url: string) =>
    ['roomPlan', 'resources', authScope, url] as const,
  event: (authScope: string, url: string) =>
    ['roomPlan', 'events', authScope, url] as const,
};

export const buildRoomPlanQueryAuthScope = (authUser?: RoomPlanQueryAuthContext | null) => {
  const propId = authUser?.prop_id == null ? 'unknown-property' : String(authUser.prop_id);
  const userId = authUser?.user_id == null ? 'unknown-user' : String(authUser.user_id);
  const logId = authUser?.log_id == null ? 'unknown-session' : String(authUser.log_id);
  return `${propId}:${userId}:${logId}`;
};

export const isRoomPlanForceRefresh = (payload: any = {}) =>
  Boolean(payload?.[ROOMPLAN_FORCE_REFRESH_FLAG] || payload?.forceRefresh);

export const stripRoomPlanInternalQueryFlags = (payload: any = {}) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;

  const {
    [ROOMPLAN_FORCE_REFRESH_FLAG]: _roomplanForceRefresh,
    forceRefresh: _forceRefresh,
    ...cleanPayload
  } = payload;

  return cleanPayload;
};

export const fetchRoomPlanQuery = async (
  scope: RoomPlanQueryScope,
  url: string,
  headers: any,
  forceRefresh = false,
  authScope = buildRoomPlanQueryAuthScope()
): Promise<ApiResponse> => {
  const queryKey = scope === 'resource'
    ? roomPlanQueryKeys.resource(authScope, url)
    : roomPlanQueryKeys.event(authScope, url);

  return queryClient.fetchQuery<ApiResponse>({
    queryKey,
    queryFn: () => APIGetRequest(url, headers),
    staleTime: forceRefresh ? 0 : ROOMPLAN_QUERY_STALE_TIME[scope],
    cacheTime: ROOMPLAN_QUERY_CACHE_TIME,
  });
};

export const invalidateRoomPlanQueries = () =>
  queryClient.invalidateQueries({ queryKey: roomPlanQueryKeys.all });
